import { api } from '@/lib/api';
import { isStandalone } from '@/lib/app-mode';
import { isIosSafari } from '@/lib/install';

/**
 * إشعاراتُ الجوال لهذا الجهاز (0059 — وجولة الجوال 26 سبتمبر 2026).
 *
 * ثلاثُ قواعد تحكم هذا الملف، وكلٌّ منها عطلٌ رآه صاحبُ المنصة على آيفون:
 *
 *  1. **طلبُ الإذن أولُ نداءٍ غير متزامن في معالج الضغطة.** سفاري (التطبيق المثبَّت، iOS 16.4+)
 *     لا يقبل `Notification.requestPermission()` إلا داخل لمسة المستخدم نفسِها؛ وبعد انتظار
 *     شبكةٍ تضيع اللمسة فيُرفض الطلبُ صامتاً ويبدو الزرُّ كأنه لا يعمل. لذلك المفتاحُ العامّ
 *     يُجلب **مسبقاً** (`pushState` عند فتح البطاقة) ويُحفظ في ذاكرة الوحدة.
 *  2. **آيفون خارج التطبيق المثبَّت ليس «غير مدعوم»** بل «ثبّته أولاً» — سفاري العادي بلا
 *     `PushManager` أصلاً، فالحالةُ `ios-install` تقول الخطوات بدل رسالةٍ عامّة.
 *  3. **الاشتراكُ يُعاد تثبيتُه عند كل فتح** (`syncPushSubscription`): صفُّ الخادم قد يُمحى
 *     (410، خمسُ إخفاقات، مفتاحٌ تغيّر) والجهازُ يظنّ نفسه مشتركاً فلا يصله شيء. والمسارُ في
 *     الخادم `upsert` على النقطة — متساوي الأثر.
 */
export type PushState = 'unsupported' | 'ios-install' | 'unconfigured' | 'denied' | 'off' | 'on';

const O = { institutionId: null } as const;

/** `undefined` = لم يُجلب بعد؛ `null` = المنصة لم تُفعّل الإشعارات */
let cachedKey: string | null | undefined;
let keyInFlight: Promise<string | null> | null = null;

/** للاختبار وحده */
export function resetPushKeyCache(): void { cachedKey = undefined; keyInFlight = null; }

/** المفتاحُ العامّ من الذاكرة، أو من الخادم مرّةً واحدة */
export async function loadPushKey(): Promise<string | null> {
  if (cachedKey !== undefined) return cachedKey;
  keyInFlight ??= api.get<{ publicKey: string | null }>('/public/push-key', O)
    .then((r) => { cachedKey = r.publicKey ?? null; return cachedKey; })
    .finally(() => { keyInFlight = null; });
  return keyInFlight;
}

export function keyBytes(b64: string): Uint8Array {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a) return true; // المتصفّحُ لا يقول بأي مفتاحٍ اشترك — لا نهدم اشتراكاً لا نعرف عنه شيئاً
  const x = new Uint8Array(a);
  return x.length === b.length && x.every((v, i) => v === b[i]);
}

export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** أين نحن من الدعم؟ آيفون في سفاري العادي يحتاج التثبيت قبل أي شيء */
export function pushEnvironment(): 'supported' | 'ios-install' | 'unsupported' {
  if (pushSupported()) return 'supported';
  return isIosSafari() && !isStandalone() ? 'ios-install' : 'unsupported';
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

export async function pushState(): Promise<PushState> {
  const env = pushEnvironment();
  if (env !== 'supported') return env;
  const publicKey = await loadPushKey();
  if (!publicKey) return 'unconfigured';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

async function register(sub: PushSubscription): Promise<void> {
  const j = sub.toJSON();
  await api.post('/me/push-subscriptions', {
    endpoint: j.endpoint, keys: { p256dh: j.keys?.p256dh ?? '', auth: j.keys?.auth ?? '' }, userAgent: navigator.userAgent.slice(0, 300),
  }, O);
}

/** اشتراكٌ بالمفتاح الحالي: القائمُ إن طابق، وإلا يُلغى ويُنشأ من جديد */
async function subscriptionFor(reg: ServiceWorkerRegistration, publicKey: string): Promise<{ sub: PushSubscription; renewed: boolean }> {
  const want = keyBytes(publicKey);
  const current = await reg.pushManager.getSubscription();
  if (current && sameKey(current.options?.applicationServerKey, want)) return { sub: current, renewed: false };
  if (current) await current.unsubscribe().catch(() => false);
  return { sub: await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: want }), renewed: true };
}

/**
 * يُنادى من معالج الضغطة مباشرةً. **`requestPermission` أولُ نداءٍ غير متزامن** — لا شبكةَ قبله.
 * المفتاحُ من الذاكرة (جلبه `pushState` عند فتح البطاقة)؛ وإن عُرف أن المنصة بلا مفتاح يُردّ
 * فوراً بلا سؤالٍ عن إذنٍ لا فائدة منه.
 */
export async function enablePush(): Promise<PushState> {
  const env = pushEnvironment();
  if (env !== 'supported') return env;
  if (cachedKey === null) return 'unconfigured';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'off';
  const publicKey = await loadPushKey();
  if (!publicKey) return 'unconfigured';
  const reg = (await registration()) ?? (await navigator.serviceWorker.register('/sw.js'));
  await navigator.serviceWorker.ready;
  const { sub } = await subscriptionFor(reg, publicKey);
  await register(sub);
  return 'on';
}

/**
 * إعادةُ تثبيت الاشتراك عند فتح التطبيق بعد الدخول: إن كان الإذنُ ممنوحاً وللمتصفّح اشتراكٌ
 * أُعيد إرساله (الخادمُ يحفظه صفّاً واحداً)، وإن تغيّر المفتاحُ العامّ أُعيد إنشاؤه.
 * لا يطلب إذناً ولا ينشئ اشتراكاً لجهازٍ لم يُفعَّل عليه — ذلك قرارُ صاحبه بلمسة.
 */
export async function syncPushSubscription(): Promise<'skipped' | 'kept' | 'renewed'> {
  if (!pushSupported() || Notification.permission !== 'granted') return 'skipped';
  const reg = await registration();
  if (!reg || !(await reg.pushManager.getSubscription())) return 'skipped';
  const publicKey = await loadPushKey();
  if (!publicKey) return 'skipped';
  const { sub, renewed } = await subscriptionFor(reg, publicKey);
  await register(sub);
  return renewed ? 'renewed' : 'kept';
}

export async function disablePush(): Promise<PushState> {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.post('/me/push-subscriptions/remove', { endpoint: sub.endpoint }, O).catch(() => undefined);
    await sub.unsubscribe().catch(() => false);
  }
  return 'off';
}

/** «أرسل إشعاراً تجريبياً إلى هذا الجهاز» — مرّةً في الدقيقة (الخادمُ يحكم) */
export function sendTestPush(): Promise<{ devices: number; sent: number }> {
  return api.post<{ devices: number; sent: number }>('/me/push-subscriptions/test', undefined, O);
}

/* ─── شارة أيقونة التطبيق (Badging API) — اختيارية: كلُّ نداءٍ محروسٌ ولا يرمي ─── */
type BadgeNavigator = Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };

export function setAppBadge(n: number): void {
  if (typeof navigator === 'undefined') return;
  const nav = navigator as BadgeNavigator;
  try {
    if (n > 0) void nav.setAppBadge?.(n)?.catch?.(() => undefined);
    else void nav.clearAppBadge?.()?.catch?.(() => undefined);
  } catch { /* متصفّحٌ يعلن الدالة ويرفضها — الشارةُ زينةٌ لا شرط */ }
}

/* ─── «فعّل التنبيهات على هذا الجهاز» بعد التثبيت — «ليس الآن» يُحفظ أسبوعين ─── */
const NUDGE_KEY = 'rafrafa.push.nudgeDismissedAt';
const NUDGE_QUIET_MS = 14 * 86_400_000;

export function pushNudgeDismissed(now = Date.now()): boolean {
  try {
    const v = Number(localStorage.getItem(NUDGE_KEY) ?? 0);
    return v > 0 && now - v < NUDGE_QUIET_MS;
  } catch { return false; }
}

export function dismissPushNudge(now = Date.now()): void {
  try { localStorage.setItem(NUDGE_KEY, String(now)); } catch { /* التخزينُ محجوب — تعود البطاقةُ في الفتح التالي */ }
}
